import { QueryCache, QueryClient, queryOptions } from "@tanstack/react-query";
import { notify } from "../app/toast";
import { ApiError, api } from "./client";
import type {
  AccountInsights,
  CommentEvent,
  ContactDirectory,
  ConversationMessage,
  ConversationView,
  Dashboard,
  MediaInsights,
  PostDetail,
  OnboardingStatus,
  RuleFunnel,
} from "./types";

/** How often what the panel shows is refreshed from the server. */
const REFRESH_INTERVAL_MS = 15_000;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // A failed refresh keeps showing what was there before and says so once: the panel used
    // to go silent and leave stale figures looking current.
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) return;
      notify(error.message || "No se pudo actualizar el panel", true);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      // An expired session is not retried: the client is already on its way to sign-in.
      retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
    },
  },
});

/**
 * Everything the panel paints on every screen: account, figures, posts and rules.
 *
 * It is one request because the server builds it in one go, and it is polled so a comment
 * that arrives while the panel is open shows up without reloading.
 */
export const dashboardQuery = queryOptions({
  queryKey: ["dashboard"],
  queryFn: () => api.get<Dashboard>("/api/dashboard"),
  refetchInterval: REFRESH_INTERVAL_MS,
});

/** The people directory, only fetched on its own screen. */
export const contactsQuery = queryOptions({
  queryKey: ["contacts"],
  queryFn: () => api.get<ContactDirectory>("/api/contacts"),
});

/**
 * The log narrowed to one outcome.
 *
 * It is asked of the server rather than filtered here because the dashboard only carries
 * the last rows: with two thousand comments the failures are scattered among all of them.
 */
export const eventLogQuery = (outcome: string) =>
  queryOptions({
    queryKey: ["events", outcome],
    queryFn: async () =>
      (await api.get<{ events: CommentEvent[] }>(`/api/events?outcome=${encodeURIComponent(outcome)}`)).events,
    refetchInterval: REFRESH_INTERVAL_MS,
  });

/** One conversation's whole thread. */
export const threadQuery = (conversationId: number) =>
  queryOptions({
    queryKey: ["conversations", conversationId],
    queryFn: () =>
      api.get<{ conversation: ConversationView; messages: ConversationMessage[] }>(
        `/api/conversations/${conversationId}`,
      ),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

/** A rule's funnel: one query per rule, so it is only computed when it is opened. */
export const funnelQuery = (ruleId: number) =>
  queryOptions({
    queryKey: ["funnel", ruleId],
    queryFn: async () => (await api.get<{ funnel: RuleFunnel | null }>(`/api/metrics/${ruleId}`)).funnel,
  });

/**
 * Every post's statistics, read from the database: opening the screen costs Meta nothing.
 * The background job keeps them fresh, so a slow poll is plenty.
 */
export const mediaInsightsQuery = queryOptions({
  queryKey: ["media-insights"],
  queryFn: async () => (await api.get<{ insights: MediaInsights[] }>("/api/media/insights")).insights,
  refetchInterval: 60_000,
});

/** One post's page: its numbers and what the automations caught on it. */
export const postDetailQuery = (mediaId: string) =>
  queryOptions({
    queryKey: ["post", mediaId],
    queryFn: () => api.get<PostDetail>(`/api/media/${encodeURIComponent(mediaId)}`),
  });

/** The account over the last four weeks. The server caches it for an hour. */
export const accountInsightsQuery = queryOptions({
  queryKey: ["account-insights"],
  queryFn: async () => (await api.get<{ account: AccountInsights }>("/api/insights/account")).account,
  staleTime: 10 * 60_000,
});

/** The addresses and token Meta's console needs pasted into it. */
export const onboardingQuery = queryOptions({
  queryKey: ["onboarding"],
  queryFn: () => api.get<OnboardingStatus>("/api/onboarding/status"),
});
